<template>
    <div v-if="!!actualRelaysLength" class="device-controls__block">
        <div class="device-controls__row device-controls__row--title">
            <span class="dci__title">
                <span class="sg-vrm-icon_114_switches-24px"></span>
                {{ $t('dashboard.dci.switches', { i18nFallback: 'Switches' }) }}
            </span>
            <div v-if="isWaitingForResponse" class="dci__status">
                <spinner />
                {{ $t('dashboard.dci.loading') }}
            </div>
        </div>
        <div
            v-if="toggleRelayTimer0.nextChange !== null || toggleRelayTimer1.nextChange !== null"
            :style="{ minHeight: bodyHeight + 'px' }"
            class="device-controls__body device-controls__body--switches device-controls__body--switches__warning"
        >
            <div
                class="device-controls__body--switches__warning-text"
                :style="
                    actualRelaysLength === 1 ? { marginTop: '-1.5rem', marginBottom: '1rem' } : {}
                "
            >
                <span class="sg-vrm-icon_012_alarm-32px dci__warning__icon"></span>
                <div
                    v-html="
                        timerMessage(
                            toggleRelayTimer0.nextChange === null
                                ? toggleRelayTimer1
                                : toggleRelayTimer0,
                        )
                    "
                ></div>
            </div>
            <div class="device-controls__body--switches__warning-buttons">
                <div
                    @click="
                        toggleRelayTimer0.nextChange === null
                            ? toggleRelayTimer1.clearChangeTimer()
                            : toggleRelayTimer0.clearChangeTimer()
                    "
                >
                    {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                </div>
                <div
                    @click="
                        toggleRelayTimer0.nextChange === null
                            ? toggleRelayTimer1.confirmChange()
                            : toggleRelayTimer0.confirmChange()
                    "
                >
                    {{ $t('common.confirm', { i18nFallback: 'Confirm' }) }}
                </div>
            </div>
        </div>
        <div
            v-else
            ref="switchesBody"
            class="device-controls__body device-controls__body--switches"
        >
            <span v-if="relay0Change.error" class="dci__warning dci__warning--inline">
                {{ $t('dashboard.dci.errors.' + relay0Change.error) }}
            </span>
            <span v-if="relay1Change.error" class="dci__warning dci__warning--inline">
                {{ $t('dashboard.dci.errors.' + relay1Change.error) }}
            </span>
            <div
                v-for="(_, idx) in manualRelays"
                :key="idx"
                class="device-controls__row device-controls__row--switches"
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
            >
                <span class="dci__action">
                    {{ $t('dashboard.dci.relay', { i18nFallback: 'Relay' }) }} {{ +idx + 1 }}
                </span>
                <vrm-controlled-toggle
                    v-if="!isMobile"
                    :value="relays[idx]"
                    :disabled="isWaitingForResponse"
                    @input="value => setRelayWithTimer(idx, value)"
                />
                <vrm-new-toggle
                    v-if="isMobile"
                    :value="relays[idx]"
                    :disabled="isWaitingForResponse"
                    @input="changeShowPopUpSwitch(idx)"
                />
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

import { ngInject } from '@/vue/decorators/angular-interop';
import { createTimer } from './components/Timer';

import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle';
import Clock from './components/Clock.vue';
import Spinner from './components/Spinner';
import VrmNewToggle from '../../toggles/VrmNewToggle.vue';

import {
    getRelaysSubscriptions,
    publishToPath,
    subscribeToInfo,
    unsubscribeFromInfo,
} from './utils';

import { newMqttWriteValue, initiateValueLoading, resolveValue } from './writeValueUtils';
import ShowPopUpControlsMobileMixin from './components/ShowPopUpControlsMobileMixin';

@Component({
    components: {
        Clock,
        VrmControlledToggle,
        Spinner,
        VrmNewToggle,
    },
    mixins: [ShowPopUpControlsMobileMixin],
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
export default class Switches extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    ApiService;

    @Prop({ type: String, required: true })
    identifier;
    @Prop({ type: Number, required: true })
    installationId;
    @Prop({ type: Number, required: true })
    instance;
    @Prop({ type: Number, required: true })
    timerLimit;
    @Prop()
    popUpData;

    SUBSCRIPTIONS = getRelaysSubscriptions();
    // controlled by mqtt
    relays = {
        0: null,
        1: null,
    };
    relayFunction = {
        0: null,
        1: null,
    };

    // inherited from createTimer at mount
    toggleRelayTimer0 = {};
    toggleRelayTimer1 = {};

    bodyHeight = this.actualRelaysLength === 1 ? 30 : 76;

    created() {
        createTimer.call(this, 'toggleRelayTimer0', this.timerLimit);
        createTimer.call(this, 'toggleRelayTimer1', this.timerLimit);
    }

    relay0Change = newMqttWriteValue({ varName: 'relays.0' });
    relay1Change = newMqttWriteValue({ varName: 'relays.1' });

    mounted() {
        this.subscribeToInfo = subscribeToInfo.bind(this);
        this.unsubscribeFromInfo = unsubscribeFromInfo.bind(this);
        this.publishToPath = publishToPath.bind(this);
        this.initiateValueLoading = initiateValueLoading.bind(this);
        this.resolveValue = resolveValue.bind(this);
        this.subscribeToInfo();
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        window.clearTimeout(this.relay0Change.timer);
        window.clearTimeout(this.relay1Change.timer);
        this.unsubscribeFromInfo();
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('batteryLifeState')
    onBatteryLifeState() {
        this.resolveValue(this.batteryLifeStateChange, +this.batteryLifeState);
    }

    @Watch('relays.0')
    onRelay0() {
        this.resolveValue(this.relay0Change, +this.relays[0]);
    }

    @Watch('relays.1')
    onRelay1() {
        this.resolveValue(this.relay1Change, +this.relays[1]);
    }

    get isWaitingForResponse() {
        return this.relay0Change.busy || this.relay1Change.busy;
    }

    setRelayWithTimer(relayNr, value) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.toggleRelayTimers[relayNr].callFuncWithTimer({ relayNr, value }, this.setRelay);
    }

    setRelay({ relayNr, value }) {
        if (relayNr === '0') {
            this.relay0Change.oldState = this.relays[0];
            this.relay0Change.newState = +value;
            this.initiateValueLoading(this.relay0Change);
        } else if (relayNr === '1') {
            this.relay1Change.oldState = this.relays[1];
            this.relay1Change.newState = +value;
            this.initiateValueLoading(this.relay1Change);
        }
        this.publishToPath(
            {
                dbusPath: `/Relay/${relayNr}/State`,
                callbackIdentifier: `setRelay_${this.instance}_${relayNr}`,
            },
            { value: +value },
            this.relays[relayNr],
        );
    }

    getCallbackIdentifier(varName) {
        return `get_${varName}OfRelays_${this.instance}`;
    }

    get manualRelays() {
        let validRelays = { ...this.relays };

        // check manual availability of the relays
        Object.keys(this.relayFunction).forEach(relayIdx => {
            if (this.relayFunction[relayIdx] !== 2) {
                delete validRelays[relayIdx];
            }
        });

        const filteredRelays = Object.entries(validRelays).filter(([, value]) => value !== null);
        return Object.fromEntries(filteredRelays);
    }

    get actualRelaysLength() {
        return Object.values(this.manualRelays).length;
    }

    get toggleRelayTimers() {
        return [this.toggleRelayTimer0, this.toggleRelayTimer1];
    }

    timerMessage(timer) {
        const secondsLeft = this.timerLimit - timer.changeSeconds;
        return this.$t(
            +timer.nextChange.value === 0
                ? 'dashboard.dci.countdown_timer_disabled'
                : 'dashboard.dci.countdown_timer',
            {
                nextState: this.$t('dashboard.dci.relay') + ' ' + (+timer.nextChange.relayNr + 1),
                seconds: secondsLeft,
            },
        );
    }

    matchBodyHeight() {
        if (this.$refs.switchesBody !== undefined)
            this.bodyHeight = this.$refs.switchesBody.clientHeight;
    }

    get mqttPathObj() {
        return {
            identifier: this.identifier,
            dbusServiceType: 'system',
            instance: this.instance,
        };
    }

    changeShowPopUpSwitch(idx) {
        this.$emit('updateShowPopUp', {
            popUpType: 'SWITCH-RELAYS',
            showPopUp: true,
            isOn: this.relays[idx],
            relayIdx: idx,
            instance: this.instance,
        });
    }
}
</script>
